import { type FC } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { InlineLoading, InlineNotification } from '@carbon/react';
import { activePreDrillWorkspaceHrefAtom } from 'components/pre-drill-workspace-launcher';
import { PreDrillWorkspaceContent } from './pre-drill-workspace-content';
import { usePreDrillWorkspaceResourceAtom } from './atoms';

interface PreDrillWorkspaceShellProps {
  /** The workspace to show, given by a host that owns the choice (the geometry
   *  tearsheet); otherwise read from the launcher atom. */
  href?: string | null;
  /** Leaving the workspace. Defaults to clearing the launcher atom. */
  onLeave?: () => void;
  /** Hosted inside a tearsheet rather than laid out on a page of its own. */
  embedded?: boolean;
  /** The well the sheet was opened for, for its header. */
  wellName?: string | null;
  /** The Well Workspace hosting the sheet; every geometry address is built from it. */
  wellWorkspaceId?: number | null;
}

// In-page host for the active pre-drill workspace (local atom state, no route).
// Fetches the thin show; sections fetch sub-resources. Back clears the atom.
export const PreDrillWorkspaceShell: FC<PreDrillWorkspaceShellProps> = ({
  href: hrefProp,
  onLeave,
  embedded = false,
  wellName,
  wellWorkspaceId,
}) => {
  const activeHref = useAtomValue(activePreDrillWorkspaceHrefAtom);
  const clearActive = useSetAtom(activePreDrillWorkspaceHrefAtom);
  const href = hrefProp === undefined ? activeHref : hrefProp;

  const resourceAtom = usePreDrillWorkspaceResourceAtom(href);
  // Setter re-GETs href; passed as refetchShow so lifecycle mutations
  // (confirm / refresh / promote) refresh the thin show.
  const [resource, refetchShow] = useAtom(resourceAtom);

  if (!href) return null;

  if (resource.loading && !resource.data) return <InlineLoading />;

  if (resource.data && 'error' in resource.data) {
    return (
      <InlineNotification
        kind="error"
        title={resource.data.error}
        lowContrast
      />
    );
  }

  if (!resource.data) {
    return (
      <InlineNotification
        kind="error"
        title="Something went wrong"
        lowContrast
      />
    );
  }

  return (
    <PreDrillWorkspaceContent
      workspace={resource.data}
      refetchShow={refetchShow}
      onLeave={onLeave ?? (() => clearActive(null))}
      embedded={embedded}
      wellName={wellName}
      wellWorkspaceId={wellWorkspaceId}
    />
  );
};
